import { Controller } from "@hotwired/stimulus"
import ProgressBar from "../helpers/progress_bar"
import RequestHelper from "../helpers/request_helper"

const ENTER_KEY = 13
// History.js announces `statechange` through jQuery's trigger, and jquery-ujs announces
// `ajax:beforeSend` / `ajax:success` the same way, so none of the three reaches a data-action.
const JQUERY_NAMESPACE = ".productShowcase"

export default class extends Controller {
  static get targets() { return ["productImages", "preload"]; }

  connect() {
    this.preloadAlternateImages()

    this.applyPushedState = this.applyPushedState.bind(this)
    $(window).on(`statechange${JQUERY_NAMESPACE}`, this.applyPushedState)

    $(this.element).
      on(`ajax:beforeSend${JQUERY_NAMESPACE}`, () => ProgressBar.start()).
      on(`ajax:success${JQUERY_NAMESPACE}`, (event, response) => this.pushFetchedImages(response))
  }

  disconnect() {
    $(window).off(JQUERY_NAMESPACE)
    $(this.element).off(JQUERY_NAMESPACE)
  }

  changeProductImage(event) {
    event.preventDefault()

    // Both show actions render the bare partial only for a request that looks like AJAX.
    const options = RequestHelper.defaultRequestOptions()
    options.headers = Object.assign({}, options.headers, { "X-Requested-With": "XMLHttpRequest" })

    // event.currentTarget.dataset.path lives on the chevron arrow the click/keypress fired on,
    // one of two per showcase, not on this controller's own element -- not a Values API candidate
    // (D8: Values API is scoped to the controller's root element, not an arbitrary event target).
    fetch(event.currentTarget.dataset.path, options).
      then(response => {
        if (!response.ok) { throw new Error(`Product image lookup failed (${response.status})`); }
        return response.text()
      }).
      // ProductsController#show renders this XHR body with `partial: "products/product_image",
      // layout: false`; the partial's only dynamic content goes through `image_tag`/`content_tag`/
      // `link_to` (see ProductDecorator#image_link, #image_tag), all auto-escaping, no
      // `raw`/`html_safe`/`!=` in the chain -- server-rendered HTML from our own origin.
      then(html => { this.productImagesTarget.outerHTML = html }).
      catch(this.logFailedCall)
  }

  changeProductImageOnEnter(event) {
    if (event.which !== ENTER_KEY) { return; }

    this.changeProductImage(event)
  }

  // The variant links are jquery-ujs `remote: true`, so the swap waits on the fetched image being
  // decoded before the url moves — otherwise the address bar leads the picture.
  pushFetchedImages(response) {
    // S1: `fragment.content` is a <template>'s inert DocumentFragment -- it is never attached to
    // `document`, never rendered, and (per the HTML spec) resources it references are not
    // fetched, so this line has no injection surface regardless of what `response` contains; it
    // exists only to read two values back out below. The actual display sink for this same
    // `response` string is the outerHTML assignment in applyPushedState below, classified there.
    const fragment = document.createElement("template")
    fragment.innerHTML = response
    const image = new Image()

    image.onload = () => History.pushState({ response: response }, "Loop & Tie", fragment.content.firstElementChild.dataset.url)
    image.src = fragment.content.querySelector("img").getAttribute("src")
  }

  applyPushedState() {
    const state = History.getState()
    if (state === null) { return; }

    // Back can land here with a state this page never pushed; leave the images alone rather than
    // rendering an empty result over them.
    if (state.data.response !== undefined) {
      // `state.data.response` is the same jquery-ujs-fetched "products/product_image" partial
      // body pushed by pushFetchedImages above -- server-rendered, our own origin, no
      // `raw`/`html_safe`/`!=` in its render chain. See the fetch().then() comment above for the
      // other sink that shares this same content.
      this.productImagesTarget.outerHTML = state.data.response
    }

    ProgressBar.stop()
  }

  preloadAlternateImages() {
    JSON.parse(this.preloadTarget.dataset.preload).forEach(url => { new Image().src = url })
  }

  logFailedCall(response) {
    console.warn(response)
  }
}
